import { useLazyQuery } from '@apollo/client';
import {
  GET_INNOVATION_CONTENT_STATISTICS,
  InnovationType,
} from '@frontend/data-access';
import { getLinkLabel } from '@frontend/shared/util';
import { Input, Select, Table } from 'antd';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useState } from 'react';

type FilterSelectionProps = 'week' | 'month' | 'quarter' | 'year';

const calculateDateRange = (period: FilterSelectionProps) => {
  const endDate = dayjs().format('YYYY-MM-DD');
  let startDate;

  switch (period) {
    case 'week':
      startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      break;
    case 'month':
      startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
      break;
    case 'quarter':
      startDate = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
      break;
    case 'year':
      startDate = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
      break;
    default:
      throw new Error('Invalid period');
  }
  return { startDate, endDate };
};

const InnovationContentStats = () => {
  const { t } = useTranslation('admin');
  const [filterPeriod, setFilterPeriod] =
    useState<FilterSelectionProps>('week');
  const [searchTerm, setSearchTerm] = useState(''); // New state for search term

  // Calculate default date range for last week
  const { startDate: defaultStartDate, endDate: defaultEndDate } = useMemo(
    () => calculateDateRange('week'),
    []
  );
  const filterSelections = useMemo(
    () => [
      { label: t('last_week'), value: 'week' },
      { label: t('last_30_days'), value: 'month' },
      { label: t('last_3_months'), value: 'quarter' },
      { label: t('last_year'), value: 'year' },
    ],
    [t]
  );

  const [getData, { data, loading }] = useLazyQuery<{
    innovationSpaceContentStatistics: {
      name: string;
      type: InnovationType;
      ideas: { count: number; status: string }[];
      challenges: number;
      posts: { count: number; status: string }[];
      users: number;
    }[];
  }>(GET_INNOVATION_CONTENT_STATISTICS);

  // Fetch data on component mount with default date range
  useEffect(() => {
    getData({
      variables: {
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      },
    });
  }, [getData, defaultStartDate, defaultEndDate]);

  // Handle data fetching when filter selection changes
  const handleFilterChange = useCallback(
    (value: FilterSelectionProps) => {
      setFilterPeriod(value);
      const { startDate, endDate } = calculateDateRange(value);
      getData({
        variables: { startDate, endDate },
      });
    },
    [getData]
  );

  const columns = useMemo(
    () => [
      {
        title: t('innovation_spaces'),
        dataIndex: 'name',
        key: 'name',
        width: 330,
      },
      {
        title: t('type'),
        dataIndex: 'type',
        key: 'type',
        render: (type: InnovationType) => getLinkLabel(type.toLowerCase()),
      },
      {
        title: t('no_of_users'),
        dataIndex: 'users',
        key: 'users',
      },
      {
        title: t('no_of_ideas'),
        dataIndex: 'ideas',
        key: 'ideas',
        render: (ideas: { count: number; status: string }[]) =>
          ideas
            .filter(
              (item) =>
                item.status === 'APPROVED' || item.status === 'PUBLISHED'
            )
            .reduce((acc, curr) => acc + curr.count, 0),
      },
      {
        title: t('no_of_challenges'),
        dataIndex: 'challenges',
        key: 'challenges',
      },
      {
        title: t('no_of_posts_events'),
        dataIndex: 'posts',
        key: 'posts',
        render: (posts: { count: number; status: string }[]) =>
          posts
            .filter(
              (item) =>
                item.status === 'APPROVED' || item.status === 'PUBLISHED'
            )
            .reduce((acc, curr) => acc + curr.count, 0),
      },
    ],
    [t]
  );

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!data?.innovationSpaceContentStatistics || !searchTerm) {
      return data?.innovationSpaceContentStatistics || [];
    }
    return data.innovationSpaceContentStatistics.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Input
          placeholder={t('search_by_name')}
          className="!w-64"
          value={searchTerm} // Controlled input
          onChange={(e) => setSearchTerm(e.target.value)} // Update search term on input change
        />
        <Select
          defaultValue={filterPeriod}
          onChange={(value) =>
            handleFilterChange(value as FilterSelectionProps)
          }
          options={filterSelections}
        />
      </div>
      <Table
        columns={columns}
        loading={loading}
        dataSource={filteredData} // Pass the filtered data
        rowKey="name"
      />
    </div>
  );
};

export default InnovationContentStats;
